/**
 * Pure text helpers shared by the local structuring engines (voice notes and
 * the ficha). Bilingual (es/en), no i18n, no DOM, no network: deterministic and
 * unit-testable.
 */

/** Accent folding table. One char in, one char out: offsets stay valid. */
const ACCENTS: Record<string, string> = {
  á: 'a', à: 'a', ä: 'a', â: 'a', ã: 'a',
  é: 'e', è: 'e', ë: 'e', ê: 'e',
  í: 'i', ì: 'i', ï: 'i', î: 'i',
  ó: 'o', ò: 'o', ö: 'o', ô: 'o', õ: 'o',
  ú: 'u', ù: 'u', ü: 'u', û: 'u',
  ñ: 'n', ç: 'c', ý: 'y',
};

/**
 * Lowercase + accent-fold preserving length, so a match index on the folded
 * text points at the same character in the original.
 */
export function fold(text: string): string {
  return text
    .normalize('NFC')
    .toLowerCase()
    .replace(/[áàäâãéèëêíìïîóòöôõúùüûñçý]/g, (ch) => ACCENTS[ch] ?? ch);
}

/** Operative verbs and markers → key steps (es/en) */
export const STEP_MARKERS = [
  // en
  'incision', 'dissect', 'ligation', 'ligat', 'division', 'divide', 'section',
  'closure', 'close', 'suture', 'sutur', 'resection', 'resect', 'anastomos',
  'drain', 'hemostasis', 'extraction', 'extract', 'removal', 'remove',
  'placement', 'place', 'identification', 'identif', 'opening', 'open',
  'access', 'pneumoperitoneum', 'exploration', 'explor', 'irrigation',
  'lavage', 'aspiration', 'aspirat', 'clipping', 'clipped', 'clips',
  'coagulation', 'coagulat', 'repair', 'plasty', 'reduction', 'reduc',
  'fixation', 'fix', 'mobilization', 'mobiliz', 'approach', 'evisceration',
  'inspection', 'inspect', 'transect', 'technique',
  // es
  'incide', 'incidiendo', 'diseca', 'diseccion', 'disecando', 'ligadura',
  'liga ', 'ligando', 'secciona', 'seccion', 'cierre', 'cierra', 'sutura',
  'suturando', 'reseca', 'reseccion', 'anastomosis', 'drenaje', 'hemostasia',
  'extraccion', 'extrae', 'extirpa', 'exeresis', 'coloca', 'colocacion',
  'identifica', 'apertura', 'abordaje', 'acceso', 'neumoperitoneo',
  'exploracion', 'explora', 'lavado', 'aspiracion', 'clipaje', 'coagulacion',
  'reparacion', 'repara', 'plastia', 'reduccion', 'fijacion', 'movilizacion',
  'se procede', 'se realiza', 'se practica', 'se objetiva', 'se comprueba',
  'infiltra', 'diseco', 'ligo', 'secciono', 'suturo', 'cerro', 'reviso',
];

/** Instruments and materials recognizable in free text (es/en) */
export const MATERIALS_LEXICON = [
  // en
  'trocar', 'endobag', 'endoloop', 'clips', 'stapler', 'mesh',
  'harmonic scalpel', 'ultracision', 'ligasure', 'suture',
  'vicryl', 'prolene', 'polypropylene', 'drain', 'tack', 'tacker',
  'veress needle', 'veress', 'hasson', 'maryland', 'scissors', 'forceps',
  'retractor', 'electrocautery', 'vessel seal', 'energy device',
  'gauze', 'hemostatic', 'spongostan', 'surgicel', 'fibrin',
  // es
  'grapadora', 'malla', 'sutura', 'seda', 'catgut', 'monocryl', 'pds',
  'drenaje', 'penrose', 'redon', 'aguja de veress', 'electrobisturi',
  'bisturi armonico', 'pinza', 'separador', 'gasas', 'hemostatico',
  'sonda vesical', 'sonda', 'clips metalicos', 'endograpadora', 'trocares',
];

/** Risk lexicon → critical points (es/en) */
export const RISK_LEXICON = [
  // en
  'bleeding', 'hemorrhage', 'adhesion', 'adhesions', 'injury', 'injur',
  'difficulty', 'difficult', 'risk', 'conversion', 'convert',
  'complication', 'tear', 'perforation', 'perforat', 'leak', 'stenosis',
  'ischemia', 'phlegmon', 'severe inflammation', 'friable', 'friability',
  'oozing', 'hematoma', 'tension', 'questionable viability', 'abscess',
  'dehiscence', 'necrosis', 'contamination',
  // es
  'sangrado', 'hemorragia', 'adherencia', 'lesion', 'dificultad',
  'riesgo', 'conversion', 'complicacion', 'desgarro', 'perforacion',
  'fuga', 'estenosis', 'isquemia', 'flemon', 'inflamacion severa', 'friable',
  'hematoma', 'tension', 'absceso', 'dehiscencia', 'necrosis',
  'contaminacion',
];

/** Frequent filler words stripped from the start of steps (es/en) */
const LEADING_FILLER =
  /^(then|next|afterwards|afterward|subsequently|finally|lastly|and|also|additionally|furthermore|moreover|with|the|a|an|after this|after that|at this point|luego|despues|después|a continuacion|a continuación|seguidamente|posteriormente|finalmente|por ultimo|por último|primero|y|ademas|además|tras esto|acto seguido)\s+/i;

/** Splits free text into clean sentences (short fragments are dropped). */
export function splitSentences(text: string): string[] {
  return text
    .replace(/\r/g, '')
    .split(/(?<=[.;:!?])\s+|\n+/)
    .map((f) => f.trim().replace(/^[•\-–*]\s*/, '').replace(/[.;:\s]+$/, ''))
    .filter((f) => f.length > 8);
}

export function containsAny(sentence: string, terms: string[]): boolean {
  const s = fold(sentence);
  return terms.some((term) => s.includes(fold(term)));
}

/** Strips leading connectors and capitalizes — reads as a step, not prose. */
export function capitalizeStep(sentence: string): string {
  const clean = sentence.replace(LEADING_FILLER, '').trim();
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

export interface ExtractStepsOptions {
  max?: number;
  /**
   * When no sentence carries an operative marker, use the sentence sequence as
   * the steps. Useful for an operative narrative, harmful for a summary
   * dictation ("duration 35 minutes" is not a step): off by default.
   */
  fallbackToSentences?: boolean;
}

/** Sentences carrying an operative verb/marker → ordered key steps. */
export function extractSteps(text: string, options: ExtractStepsOptions = {}): string[] {
  const { max = 7, fallbackToSentences = false } = options;
  const sentences = splitSentences(text);
  const steps = sentences.filter((f) => containsAny(f, STEP_MARKERS)).map(capitalizeStep);
  if (steps.length === 0 && fallbackToSentences && sentences.length > 0) {
    return sentences.slice(0, Math.min(max, sentences.length)).map(capitalizeStep);
  }
  return steps.slice(0, max);
}
